import { supabase } from '../lib/supabase';

/**
 * Fetch dashboard metrics from Supabase
 */
export const fetchDashboardMetrics = async () => {
  try {
    // Fetch total users count using RPC
    const { data: totalUsers, error: usersError } = await supabase?.rpc('get_total_users_count');

    if (usersError) {
      console.error('Error fetching users:', usersError);
    }

    // Fetch users created this month using RPC
    const { data: newUsersThisMonth, error: newUsersError } = await supabase?.rpc('get_new_users_this_month');

    if (newUsersError) {
      console.error('Error fetching new users:', newUsersError);
    }

    // Fetch total assets count
    const { count: totalAssets, error: assetsError } = await supabase?.from('assets')?.select('*', { count: 'exact', head: true });

    if (assetsError) {
      console.error('Error fetching assets:', assetsError);
    }

    // Fetch assets created this week
    const startOfWeek = new Date();
    startOfWeek?.setDate(startOfWeek?.getDate() - startOfWeek?.getDay());
    startOfWeek?.setHours(0, 0, 0, 0);

    const { count: newAssetsThisWeek, error: newAssetsWeekError } = await supabase?.from('assets')?.select('*', { count: 'exact', head: true })?.gte('created_at', startOfWeek?.toISOString());

    if (newAssetsWeekError) {
      console.error('Error fetching new assets:', newAssetsWeekError);
    }

    // Fetch active providers using RPC
    const { data: activeProviders, error: providersError } = await supabase?.rpc('get_active_providers_count');

    if (providersError) {
      console.error('Error fetching active providers:', providersError);
    }

    // Fetch bookings count for last 24 hours as system activity
    const last24Hours = new Date();
    last24Hours?.setHours(last24Hours?.getHours() - 24);

    const { count: recentBookings, error: bookingsError } = await supabase?.from('marketing_support_bookings')?.select('*', { count: 'exact', head: true })?.gte('created_at', last24Hours?.toISOString());

    if (bookingsError) {
      console.error('Error fetching bookings:', bookingsError);
    }

    // Calculate activity count (bookings + new assets + new users in last 24h)
    const { count: recentAssets } = await supabase?.from('assets')?.select('*', { count: 'exact', head: true })?.gte('created_at', last24Hours?.toISOString());

    const { count: recentPosts } = await supabase?.from('posts_of_week')?.select('*', { count: 'exact', head: true })?.gte('created_at', last24Hours?.toISOString());

    const { count: recentReadyToShare } = await supabase?.from('ready_to_share_items')?.select('*', { count: 'exact', head: true })?.gte('created_at', last24Hours?.toISOString());

    const systemActivity = (recentBookings || 0) + (recentAssets || 0) + (recentPosts || 0) + (recentReadyToShare || 0);

    return {
      totalUsers: totalUsers || 0,
      newUsersThisMonth: newUsersThisMonth || 0,
      activeProviders: activeProviders || 0,
      totalAssets: totalAssets || 0,
      newAssetsThisWeek: newAssetsThisWeek || 0,
      systemActivity: systemActivity || 0
    };
  } catch (error) {
    console.error('Error fetching dashboard metrics:', error);
    return {
      totalUsers: 0,
      newUsersThisMonth: 0,
      activeProviders: 0,
      totalAssets: 0,
      newAssetsThisWeek: 0,
      systemActivity: 0
    };
  }
};

/**
 * Fetch recent activity feed from Supabase
 */
export const fetchRecentActivity = async () => {
  try {
    const activities = [];

    // Fetch recent users using RPC
    const { data: recentUsers, error: usersError } = await supabase?.rpc('get_recent_users', { limit_count: 5 });

    if (!usersError && recentUsers) {
      recentUsers?.forEach(user => {
        activities?.push({
          id: `user-${user?.created_at}`,
          type: 'user_registration',
          message: `New user registered: ${user?.full_name}`,
          timestamp: formatTimestamp(user?.created_at),
          icon: 'UserPlus',
          iconColor: 'text-success',
          created_at: user?.created_at
        });
      });
    }

    // Fetch recent assets (last 5)
    const { data: recentAssets, error: assetsError } = await supabase?.from('assets')?.select('title, created_at, asset_type')?.order('created_at', { ascending: false })?.limit(5);

    if (!assetsError && recentAssets) {
      recentAssets?.forEach(asset => {
        const assetTypeLabel = asset?.asset_type === 'link' ? 'link' : 'asset';
        activities?.push({
          id: `asset-${asset?.created_at}`,
          type: 'asset_upload',
          message: `New ${assetTypeLabel} uploaded: ${asset?.title}`,
          timestamp: formatTimestamp(asset?.created_at),
          icon: 'Upload',
          iconColor: 'text-primary',
          created_at: asset?.created_at
        });
      });
    }

    // Fetch recent bookings (last 5)
    const { data: recentBookings, error: bookingsError } = await supabase?.from('marketing_support_bookings')?.select('support_type, full_name, created_at')?.order('created_at', { ascending: false })?.limit(5);

    if (!bookingsError && recentBookings) {
      recentBookings?.forEach(booking => {
        activities?.push({
          id: `booking-${booking?.created_at}`,
          type: 'booking',
          message: `Marketing support booking: ${booking?.support_type} by ${booking?.full_name}`,
          timestamp: formatTimestamp(booking?.created_at),
          icon: 'Calendar',
          iconColor: 'text-accent',
          created_at: booking?.created_at
        });
      });
    }

    // Fetch recent posts of week (last 3)
    const { data: recentPosts, error: postsError } = await supabase?.from('posts_of_week')?.select('title, created_at')?.order('created_at', { ascending: false })?.limit(3);

    if (!postsError && recentPosts) {
      recentPosts?.forEach(post => {
        activities?.push({
          id: `post-${post?.created_at}`,
          type: 'content_update',
          message: `Post of the Week updated: ${post?.title}`,
          timestamp: formatTimestamp(post?.created_at),
          icon: 'Edit',
          iconColor: 'text-muted-foreground',
          created_at: post?.created_at
        });
      });
    }

    // Fetch recent ready to share items (last 3)
    const { data: recentReadyToShare, error: readyToShareError } = await supabase?.from('ready_to_share_items')?.select('title, created_at')?.order('created_at', { ascending: false })?.limit(3);

    if (!readyToShareError && recentReadyToShare) {
      recentReadyToShare?.forEach(item => {
        activities?.push({
          id: `ready-${item?.created_at}`,
          type: 'content_update',
          message: `Ready to Share content added: ${item?.title}`,
          timestamp: formatTimestamp(item?.created_at),
          icon: 'CheckCircle',
          iconColor: 'text-success',
          created_at: item?.created_at
        });
      });
    }

    // Sort all activities by timestamp (most recent first)
    activities?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at));

    // Return top 10 most recent activities
    return activities?.slice(0, 10);
  } catch (error) {
    console.error('Error fetching recent activity:', error);
    return [];
  }
};

/**
 * Format timestamp to relative time
 */
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Unknown time';

  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return date?.toLocaleDateString();
};
